import { useEditorStore } from '@/stores/editor-store';
import type { DrawingTool } from '@/stores/editor-store';

interface ToolDef {
  readonly id: DrawingTool;
  readonly label: string;
  readonly shortcut: string;
}

const TOOLS: readonly ToolDef[] = [
  { id: 'pencil', label: 'P', shortcut: 'B' },
  { id: 'eraser', label: 'E', shortcut: 'E' },
  { id: 'fill', label: 'F', shortcut: 'G' },
  { id: 'eyedropper', label: 'I', shortcut: 'I' },
  { id: 'select', label: 'S', shortcut: 'M' },
] as const;

const TOOL_NAMES: Record<DrawingTool, string> = {
  pencil: 'Ołówek',
  eraser: 'Gumka',
  fill: 'Wypełnienie',
  eyedropper: 'Pipeta',
  select: 'Zaznacz',
};

export function Toolbar() {
  const activeTool = useEditorStore((s) => s.activeTool);
  const setActiveTool = useEditorStore((s) => s.setActiveTool);
  const clipboard = useEditorStore((s) => s.clipboard);
  const selection = useEditorStore((s) => s.selection);
  const isPasting = useEditorStore((s) => s.isPasting);
  const copySelection = useEditorStore((s) => s.copySelection);
  const startPaste = useEditorStore((s) => s.startPaste);
  const confirmPaste = useEditorStore((s) => s.confirmPaste);
  const cancelPaste = useEditorStore((s) => s.cancelPaste);

  return (
    <div className="flex items-center gap-1">
      {/* Drawing tools */}
      {TOOLS.map((tool) => (
        <button
          key={tool.id}
          onClick={() => setActiveTool(tool.id)}
          className={`flex h-8 w-8 items-center justify-center rounded-md text-xs font-bold transition-colors ${
            activeTool === tool.id
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          title={`${TOOL_NAMES[tool.id]} (${tool.shortcut})`}
        >
          {tool.label}
        </button>
      ))}

      {/* Separator */}
      <div className="mx-1 h-6 w-px bg-gray-200" />

      {/* Copy/Paste */}
      {isPasting ? (
        <>
          <button
            onClick={confirmPaste}
            className="rounded-md bg-green-600 px-2 py-1 text-xs font-medium text-white hover:bg-green-700"
            title="Wklej (Enter)"
          >
            Wklej
          </button>
          <button
            onClick={cancelPaste}
            className="rounded-md bg-gray-200 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-300"
            title="Anuluj (Esc)"
          >
            Anuluj
          </button>
        </>
      ) : (
        <>
          <button
            onClick={copySelection}
            disabled={!selection}
            className={`rounded-md px-2 py-1 text-xs font-medium ${
              selection
                ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                : 'bg-gray-50 text-gray-300'
            }`}
            title="Kopiuj (Ctrl+C)"
          >
            Kopiuj
          </button>
          <button
            onClick={startPaste}
            disabled={!clipboard}
            className={`rounded-md px-2 py-1 text-xs font-medium ${
              clipboard
                ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                : 'bg-gray-50 text-gray-300'
            }`}
            title="Wklej (Ctrl+V)"
          >
            Wklej
          </button>
        </>
      )}
    </div>
  );
}
